// external
import { useNavigate } from "react-router-dom";
import React, { useContext } from "react";
import { Tooltip } from "react-tooltip";

// components
import Heading from "components/partials/heading/heading";
import CustomButton from "components/partials/custom-button/custom-button";
import Loader from "components/partials/loader/loader";
import RegimeViewer from "./regime-viewer";
import useRegimeValidation from "../hooks/use-regime-validation";

// state managing/secruity
import { FaoRegimeContext } from "context/FaoRegimeContext";
import { FaoBuilderContext } from "context/FaoBuilderContext";

// assets
import Icons from "assets/icons";

// styling
import { STYLES, mergeStyles } from "../constants/styles.constants";

const validateCellMetrics = { Admits: true, Enrolls: true };

const ValidationStatus = ({ numFlagged }) => {
  const message = `${numFlagged} record${
    numFlagged > 1 && `s`
  } are falling out of the regime.`;
  return (
    <div className="flex items-start pb-2">
      {numFlagged === 0 ? (
        <Icons.Success aria-label="Validate Regime" className="text-red-600" />
      ) : (
        <Icons.Warning aria-label="Validate Regime" className="text-red-600" />
      )}
      <p className="font-family pl-2 font-semibold">{message}</p>
    </div>
  );
};

const MatrixList = ({ validateData }) => (
  <div className="max-h-[70vh] overflow-y-auto">
    {validateData.map((matrix, index) => (
      <RegimeViewer
        key={matrix.name}
        matrix={matrix}
        ind={index}
        isEditable={false}
        isDisplay={true}
        cellMetrics={validateCellMetrics}
      />
    ))}
  </div>
);

const PopupHeader = ({ isValidating, onClose, isLoading }) => (
  <div className="flex justify-between">
    <div className="flex items-center gap-2">
      <Heading
        variant="h2"
        customClass="mobile:text-center tablet:text-left font-semibold"
      >
        {isValidating
          ? "Regime Validation"
          : "Would you like to validate the regime?"}
      </Heading>
      <Icons.Info
        data-tooltip-id="run-new-validation"
        customClass="!p-0 !m-0 h-4 w-4 text-gray-03"
      ></Icons.Info>
      <Tooltip
        className="br-gray-05 z-50"
        id="run-new-validation"
        place="right"
        content="Run regime validation to see how many students are excluded by the current settings."
      />
    </div>
    <CustomButton
      iconBefore={<Icons.Plus customClass="h-4 w-4 rotate-45 text-blue-03" />}
      onClick={onClose}
      disabled={isLoading}
    />
  </div>
);

const ActionButtons = ({ onValidate, onNext, disabledNewValidation }) => {
  return (
    <div className="w-full">
      {disabledNewValidation && (
        <p
          className={mergeStyles(
            STYLES.paragraph,
            "!font-normal !text-red-700"
          )}
        >
          Dataset is currently refreshing. Please wait before running a new
          validation.
        </p>
      )}
      <div className="mt-1 flex w-full justify-between gap-3">
        <CustomButton
          variant="secondary"
          iconBefore={
            <Icons.Success
              aria-label="Validate Regime"
              customClass="h-full w-4"
            />
          }
          disabled={disabledNewValidation}
          onClick={onValidate}
        >
          Run New Validation
        </CustomButton>

        <CustomButton
          variant="primary"
          iconAfter={
            <Icons.Arrow aria-label="Next Step" customClass="h-full w-4" />
          }
          onClick={onNext}
        >
          Next Step
        </CustomButton>
      </div>
    </div>
  );
};

const LastValidationInfo = ({
  lastValidatedDate,
  handleLastValidation,
  isValidating,
}) => {
  return (
    <div>
      <p className="font-family text-md text-blue-03">
        Last Validated:{" "}
        {`${
          lastValidatedDate
            ? lastValidatedDate
            : "Validation never ran for this regime."
        }`}
      </p>
      <p className="font-family text-sm text-blue-03">
        Running a new validation can take up to 5 mins
      </p>
      {lastValidatedDate && !isValidating && (
        <CustomButton
          variant="text"
          size="sm"
          customClass="!justify-start underline !p-0 !h-4"
          onClick={handleLastValidation}
        >
          View last validation
        </CustomButton>
      )}
    </div>
  );
};
function FaoRegimeValidation({ setIsPopupOpen }) {
  const navigate = useNavigate();
  const { lastValidatedDate } = useContext(FaoRegimeContext);
  const { handleNewValidation, validationState, handleViewLastValidation } =
    useRegimeValidation();
  const { isValidating, isLoading, validateData, numFlagged, error } =
    validationState;
  const { modelConfig, refreshStatus } = useContext(FaoBuilderContext);

  const handlePopupClose = () => {
    setIsPopupOpen(false);
  };

  const handleNextStep = () => {
    navigate(`../fao-builder/${modelConfig.id}/tuition`);
  };

  return (
    <div className="fixed inset-0 z-[701] h-screen max-h-screen overflow-hidden">
      <div className="absolute inset-0 !bg-gray-07 opacity-60" />
      <div className="absolute left-1/2 top-2/4 -translate-x-1/2 -translate-y-1/2">
        <div className="flex flex-shrink flex-col justify-between rounded-md bg-white p-8 sm:min-w-[300px] sm:max-w-[500px] md:min-w-[500px] md:max-w-[1200px]">
          <PopupHeader
            isValidating={isValidating}
            onClose={handlePopupClose}
            isLoading={validationState.isLoading}
          />

          <LastValidationInfo
            lastValidatedDate={lastValidatedDate}
            handleLastValidation={handleViewLastValidation}
            isValidating={isValidating}
          />

          <div className="mt-5">
            {isLoading ? (
              <Loader />
            ) : (
              <>
                {error && <p>{error}</p>}
                {isValidating && !error && !isLoading && (
                  <>
                    <ValidationStatus numFlagged={numFlagged} />
                    <MatrixList validateData={validateData} />
                  </>
                )}
                <div className="mt-5 flex justify-between">
                  <ActionButtons
                    onValidate={handleNewValidation}
                    onNext={handleNextStep}
                    disabledNewValidation={
                      refreshStatus[modelConfig.id]?.isRefreshing
                    }
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default FaoRegimeValidation;
